import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Typography, List, ListItem, ListItemText,
  Pagination, Stack, Chip, FormControlLabel, Switch, Divider
} from '@mui/material';
import api from '../utils/api';

const ROWS_PER_PAGE = 20;

function RecordedEpisodesPage() {
  const { season_id } = useParams();
  const [seasonName, setSeasonName] = useState('');
  const [stationName, setStationName] = useState('');
  const [programs, setPrograms] = useState([]);
  // 分頁 / 排序 / 篩選狀態存 URL，點進節目再返回時自動還原。
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const sortDesc = searchParams.get('sort') !== 'asc';
  const translatedOnly = searchParams.get('translated') === '1';
  const navigate = useNavigate();

  const updateParams = (patch, { resetPage = true } = {}) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === '' || v === false || v == null) next.delete(k);
        else next.set(k, v);
      });
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
  };

  useEffect(() => {
    // 切換 season 時先清空舊資料，並用 active 旗標忽略已被取代的請求回應，
    // 避免畫面殘留上一個 season 的內容或發生 race condition。
    let active = true;
    setPrograms([]);
    setSeasonName('');
    setStationName('');

    api.get(`/seasons/${season_id}`)
      .then(res => {
        if (!active) return;
        const data = res.data || {};
        const episodes = data.episodes || [];
        setPrograms(episodes);
        setSeasonName(data.season_name || '');
        setStationName(data.station_name || episodes[0]?.station_name || '');
        document.title = data.season_name || '節目';
      })
      .catch(err => {
        if (!active) return;
        console.error('載入節目錯誤:', err);
      });

    return () => { active = false; };
  }, [season_id]);

  const filteredPrograms = useMemo(
    () => (translatedOnly ? programs.filter(p => p.translated) : programs),
    [programs, translatedOnly]
  );

  const sortedPrograms = [...filteredPrograms].sort((a, b) => {
    const av = a.start_time || '';
    const bv = b.start_time || '';
    return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
  });

  const paginated = sortedPrograms.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(sortedPrograms.length / ROWS_PER_PAGE));

  const formatTime = (str) => {
    if (!str || str.length < 12) return '';
    return `${str.slice(0, 4)}/${str.slice(4, 6)}/${str.slice(6, 8)} ${str.slice(8, 10)}:${str.slice(10, 12)}`;
  };

  const getDurationText = (start, end) => {
    if (!start || start.length < 14 || !end || end.length < 14) return '';
    const s = new Date(`${start.slice(0, 4)}-${start.slice(4, 6)}-${start.slice(6, 8)}T${start.slice(8, 10)}:${start.slice(10, 12)}:${start.slice(12, 14)}+09:00`);
    const e = new Date(`${end.slice(0, 4)}-${end.slice(4, 6)}-${end.slice(6, 8)}T${end.slice(8, 10)}:${end.slice(10, 12)}:${end.slice(12, 14)}+09:00`);
    const diffMin = Math.round((e - s) / 60000);
    const hours = Math.floor(diffMin / 60);
    const mins = diffMin % 60;
    return hours > 0 ? `${hours}小時${mins > 0 ? mins + '分鐘' : ''}` : `${mins}分鐘`;
  };

  const translatedCount = programs.filter(p => p.translated).length;

  // 各集標題常以系列名開頭（如「系列名　來賓A × 來賓B」），系列名頂部已顯示，
  // 去掉重複前綴只留該集獨有的部分；若去完為空則回退顯示原標題。
  const episodeLabel = (program) => {
    const title = (program?.title || '').trim();
    if (!title) return formatTime(program?.start_time);
    let rest = title;
    if (seasonName && rest.startsWith(seasonName)) {
      rest = rest.slice(seasonName.length).replace(/^[\s　・:：-]+/, '').trim();
    }
    return rest || title;
  };

  const castOf = (program) =>
    (Array.isArray(program?.person) ? program.person : [])
      .map(p => p?.name)
      .filter(Boolean)
      .join('、');

  return (
    <Box>
      {/* 系列標題與集數統計（節目資訊逐集不同，改由各集自行呈現） */}
      <Typography variant="h6" sx={{ lineHeight: 1.3 }}>{seasonName}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {stationName && `${stationName} · `}
        {programs.length} 集
        {translatedCount > 0 && ` · ${translatedCount} 集已翻譯`}
      </Typography>

      <Divider sx={{ mt: 1.5, mb: 1 }} />

      <FormControlLabel
        control={
          <Switch
            checked={translatedOnly}
            onChange={(e) => updateParams({ translated: e.target.checked ? '1' : '' })}
          />
        }
        label="已翻譯"
      />
      <List>
        {paginated.map((program) => {
          const cast = castOf(program);
          return (
            <ListItem
              key={program.id}
              button
              alignItems="flex-start"
              onClick={() => navigate(`/recorded/${program.id}`)}
            >
              <ListItemText
                primary={episodeLabel(program)}
                secondary={
                  <Box component="span" sx={{ display: 'block' }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {formatTime(program.start_time)}
                      <Chip
                        label={getDurationText(program.start_time, program.end_time)}
                        size="small"
                        variant="outlined"
                      />
                      {program.translated && (
                        <Chip label="已翻譯" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                    {cast && (
                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                        出演：{cast}
                      </Box>
                    )}
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, p) => updateParams({ page: p === 1 ? '' : String(p) }, { resetPage: false })}
          color="primary"
        />
        <Button onClick={() => updateParams({ sort: sortDesc ? 'asc' : '' })}>
          {sortDesc ? '新到舊' : '舊到新'}
        </Button>
      </Stack>
    </Box>
  );
}

export default RecordedEpisodesPage;
